import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { FeatureKey } from '@attendance/shared/schemas/settings';
import { useFeatures } from '../../stores/brandingStore.js';

/**
 * Route guard for an optional feature module. When the tenant has the feature
 * switched off, the route redirects home instead of rendering the page — so a
 * disabled module disappears from the UI *and* can't be reached by typing the
 * URL. Fail-open: an absent/legacy flag set is treated as enabled.
 *
 * `feature` takes one key or an array, and an array requires ALL of them to be
 * on — the same rule (and the same shape) as a nav item's `feature` in
 * navItems.ts and a report card's in the reports catalog. That is what lets a
 * page needing two pillars say so once instead of nesting two of these; the
 * single-key form is unchanged for every existing caller.
 */
export function FeatureRoute({
  feature,
  children,
  fallback,
}: {
  feature: FeatureKey | FeatureKey[];
  children: ReactNode;
  /** Rendered instead of the redirect, for a route that still has something to show with the feature off. */
  fallback?: ReactNode;
}): JSX.Element {
  const features = useFeatures();
  if (!featuresAllow(features, feature)) return fallback ? <>{fallback}</> : <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * THE RULE ITSELF, so a page that renders a report WITHOUT routing to it asks
 * exactly the question the route asks. `SmartReportPage` embeds named reports
 * by `?view=`, and two of them used to skip their route's gate that way
 * (`routes/reports/smart/embeddedReports.tsx`). Fail-open, like the route:
 * an absent flag is enabled.
 */
export function featuresAllow(
  features: Partial<Record<FeatureKey, boolean>>,
  feature: FeatureKey | readonly FeatureKey[]
): boolean {
  const keys: readonly FeatureKey[] = Array.isArray(feature) ? feature : [feature as FeatureKey];
  return keys.every((k) => features[k] !== false);
}
